import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';

export default function LineOfCreditPage() {
  useEffect(() => {
    track('page_view', { page: 'line_of_credit_detail' });
  }, []);

  useEffect(() => {
    document.title = 'Business Line of Credit | Revolving Credit Solutions for Businesses';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about business lines of credit. Understand how revolving credit works, qualification requirements, draw and repayment process, and how flexible credit access can benefit your business.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'business line of credit, revolving credit, flexible funding, business credit line, draw funds, credit access, business financing');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.href);
    }
  }, []);

  const faqs = [
    {
      question: 'What is a business line of credit?',
      answer: 'A business line of credit is a revolving credit facility that provides flexible access to funds up to a predetermined limit. Unlike a term loan where you receive a lump sum, a line of credit allows you to draw funds as needed, pay them back, and draw again—similar to a credit card but with higher limits and better terms. You only pay interest on the amount you actually use, not the entire credit limit.'
    },
    {
      question: 'How does a revolving line of credit work?',
      answer: 'Once approved for a line of credit, you receive access to a credit limit (e.g., $100,000). You can draw any amount up to that limit whenever needed, either through bank transfers, checks, or a linked card. As you repay what you borrowed, that credit becomes available again. The revolving nature means you have ongoing access without reapplying, as long as you maintain the account in good standing and make required payments.'
    },
    {
      question: 'What credit score is needed for a business line of credit?',
      answer: 'Most lenders require a personal credit score of 650 or higher for a business line of credit, with 680+ preferred for the best terms and higher limits. Business credit is also evaluated if established. Lines of credit typically have stricter credit requirements than some other funding types because of their revolving nature and flexibility. Strong credit history demonstrates your ability to manage ongoing credit responsibly.'
    },
    {
      question: 'How much can I qualify for with a line of credit?',
      answer: 'Business lines of credit typically range from $10,000 to $250,000, though some lenders offer higher limits for well-qualified businesses. The amount you qualify for depends on factors including annual revenue, time in business, credit profile, cash flow strength, and existing debt obligations. Many lenders use formulas based on monthly revenue or annual sales to determine appropriate credit limits.'
    },
    {
      question: 'What are the typical fees for a business line of credit?',
      answer: 'Common fees include draw fees (charged each time you access funds, typically 1-3%), maintenance fees (annual or monthly fees to keep the line open, often $50-$500), and sometimes inactivity fees if you don\'t use the line. Interest rates vary widely from 7-25% APR depending on creditworthiness and lender type. Some lenders charge origination fees upfront. Always review the complete fee structure before accepting a line of credit.'
    },
    {
      question: 'How long does it take to get approved for a line of credit?',
      answer: 'Approval timelines vary by lender type. Traditional banks may take 2-4 weeks for approval and setup. Online lenders and fintech companies often provide decisions within 1-3 business days, with access to funds shortly after. The application process typically requires more documentation than working capital loans but less than long-term financing. Having your financial documents organized can significantly speed up the process.'
    },
    {
      question: 'Can I use a business line of credit for any purpose?',
      answer: 'Yes, business lines of credit offer flexible use for virtually any business purpose including inventory purchases, covering cash flow gaps, managing seasonal fluctuations, taking advantage of supplier discounts, funding marketing campaigns, or handling unexpected expenses. The flexibility is one of the primary advantages—you have funds available when opportunities or needs arise without having to apply for new financing each time.'
    },
    {
      question: 'What happens if I don\'t use my line of credit?',
      answer: 'If you don\'t draw on your line of credit, you typically won\'t pay interest since interest is only charged on borrowed amounts. However, some lenders charge maintenance fees or inactivity fees to keep the line open, even if unused. These fees are usually disclosed upfront. Having an unused line of credit can actually benefit your business credit profile by showing available credit and low utilization, as long as you\'re comfortable with any associated fees.'
    }
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-gray-600 hover:text-amber-600 transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <span className="text-gray-900 font-semibold">Line of Credit</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-lg">
                  <i className="ri-bank-card-line text-white text-3xl" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">
                    Funding Type
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Business Line of Credit</h1>
                </div>
              </div>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Revolving credit facility providing flexible access to funds when you need them. Draw, repay, and draw again up to your credit limit—perfect for managing cash flow fluctuations and seizing opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_clicked', { cta: 'hero_prequalification', funding_type: 'line_of_credit' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-lg font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Check Your Options
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg border-2 border-gray-300 hover:border-indigo-500 transition-all whitespace-nowrap"
                >
                  Learn More
                  <i className="ri-arrow-down-line ml-2" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 px-4 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">$10K - $250K</div>
                <div className="text-sm text-gray-600">Credit Limit Range</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">1-3 Days</div>
                <div className="text-sm text-gray-600">Approval Timeline</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">Revolving</div>
                <div className="text-sm text-gray-600">Credit Type</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">650+</div>
                <div className="text-sm text-gray-600">Min Credit Score</div>
              </div>
            </div>
          </div>
        </section>

        {/* What is Line of Credit */}
        <section id="how-it-works" className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What is a Business Line of Credit?
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A business line of credit is a flexible financing solution that provides access to funds up to a predetermined credit limit. Unlike traditional term loans that provide a lump sum upfront, a line of credit works more like a credit card—you can draw funds as needed, repay them, and draw again.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  The revolving nature means you have ongoing access to capital without reapplying each time you need funds. You only pay interest on the amount you actually borrow, not the entire credit limit. This makes lines of credit ideal for managing cash flow fluctuations, covering unexpected expenses, or taking advantage of time-sensitive opportunities.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Lines of credit can be secured (backed by collateral like inventory or receivables) or unsecured (based on creditworthiness alone). Secured lines typically offer higher limits and lower rates, while unsecured lines provide faster access with less documentation but may have higher costs.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Key Features</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Revolving Access</div>
                      <div className="text-gray-600 text-sm">Draw, repay, and draw again as needed</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Pay Interest on Usage Only</div>
                      <div className="text-gray-600 text-sm">Interest charged only on borrowed amounts</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Flexible Draws</div>
                      <div className="text-gray-600 text-sm">Access funds via transfer, check, or card</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Ongoing Availability</div>
                      <div className="text-gray-600 text-sm">No need to reapply for each use</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-indigo-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Secured or Unsecured</div>
                      <div className="text-gray-600 text-sm">Options with or without collateral</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Used For */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Best Used For
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Lines of credit excel in situations requiring flexible, ongoing access to capital
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-arrow-up-down-line',
                  title: 'Cash Flow Management',
                  description: 'Bridge gaps between receivables and payables, ensuring smooth operations during revenue fluctuations'
                },
                {
                  icon: 'ri-flashlight-line',
                  title: 'Unexpected Expenses',
                  description: 'Cover emergency repairs, equipment breakdowns, or other unforeseen costs without disrupting operations'
                },
                {
                  icon: 'ri-calendar-event-line',
                  title: 'Seasonal Businesses',
                  description: 'Manage inventory and expenses during slow seasons, repay during peak revenue periods'
                },
                {
                  icon: 'ri-lightbulb-flash-line',
                  title: 'Opportunity Purchases',
                  description: 'Quickly capitalize on time-sensitive opportunities like bulk discounts or limited-time deals'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Financial Safety Net',
                  description: 'Maintain available credit for peace of mind and business continuity during uncertain times'
                },
                {
                  icon: 'ri-refresh-line',
                  title: 'Recurring Expenses',
                  description: 'Cover regular operational costs like payroll, rent, or utilities during temporary cash shortages'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 flex items-center justify-center bg-indigo-100 rounded-lg mb-6">
                    <i className={`${item.icon} text-indigo-600 text-2xl`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How a Line of Credit Works
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the lifecycle from application to ongoing use
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Application & Approval',
                    timeline: '1-3 business days',
                    description: 'Submit your application with business financials, credit information, and documentation. Lenders evaluate your creditworthiness, revenue, time in business, and cash flow patterns. Approval decisions typically come within 1-3 business days for online lenders, longer for traditional banks.'
                  },
                  {
                    step: '2',
                    title: 'Credit Limit Established',
                    timeline: 'Upon approval',
                    description: 'Once approved, you receive a credit limit based on your qualifications. This limit represents the maximum amount you can borrow at any time. The lender sets up your account with access methods such as online transfers, checks, or a linked debit card.'
                  },
                  {
                    step: '3',
                    title: 'Draw Funds as Needed',
                    timeline: 'Ongoing',
                    description: 'Access funds whenever needed up to your credit limit. Draw only what you need, when you need it. Some lenders charge a small draw fee (1-3%) each time you access funds. Interest begins accruing on the amount borrowed from the draw date.'
                  },
                  {
                    step: '4',
                    title: 'Make Payments & Reuse',
                    timeline: 'Monthly or as agreed',
                    description: 'Make minimum monthly payments or pay off balances in full. As you repay, that credit becomes available again for future draws. The revolving nature means you maintain ongoing access without reapplying, as long as you keep the account in good standing.'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-indigo-500 transition-colors">
                    <div className="flex items-start gap-6">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 text-white text-xl font-bold rounded-lg flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                          <span className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full whitespace-nowrap">
                            {item.timeline}
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Qualification Requirements */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Qualification Requirements
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                What lenders typically look for when evaluating line of credit applications
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Business Requirements</h3>
                <div className="space-y-4">
                  {[
                    'Minimum 12-24 months in business',
                    'Monthly revenue of $25,000 or more',
                    'Personal credit score of 650+',
                    'Strong cash flow and financial history',
                    'Active business bank account',
                    'No recent bankruptcies or defaults'
                  ].map((req, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-indigo-600" />
                      </div>
                      <span className="text-gray-700">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Documentation Needed</h3>
                <div className="space-y-4">
                  {[
                    '6-12 months of business bank statements',
                    'Business and personal tax returns (1-2 years)',
                    'Profit and loss statements',
                    'Balance sheet (if available)',
                    'Business licenses and registration',
                    'Personal financial statement'
                  ].map((doc, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-list-3-line text-indigo-600" />
                      </div>
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Advantages &amp; Considerations
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding both benefits and potential drawbacks of business lines of credit
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl">
                    <i className="ri-thumb-up-line text-white text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Advantages</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Flexible access to funds when needed',
                    'Pay interest only on amounts borrowed',
                    'Revolving credit—reuse as you repay',
                    'No need to reapply for each use',
                    'Helps build business credit history',
                    'Lower interest rates than credit cards',
                    'Can improve cash flow management',
                    'Available for emergencies and opportunities'
                  ].map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-emerald-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl">
                    <i className="ri-alert-line text-white text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Considerations</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Higher credit score requirements (650+)',
                    'May include draw fees (1-3% per draw)',
                    'Maintenance or annual fees may apply',
                    'Variable interest rates can fluctuate',
                    'Personal guarantee typically required',
                    'Requires strong financial documentation',
                    'Can be tempting to overuse available credit',
                    'Inactivity fees if line goes unused'
                  ].map((con, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-information-line text-amber-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Secured vs Unsecured */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Secured vs. Unsecured Lines of Credit
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the two main types and their differences
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl p-8 border-2 border-indigo-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-indigo-100 rounded-xl">
                    <i className="ri-shield-check-line text-indigo-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Secured Line of Credit</h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Backed by collateral such as inventory, equipment, receivables, or real estate. The collateral reduces lender risk, often resulting in better terms.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">Higher credit limits available</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">Lower interest rates (7-15% typical)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">Easier qualification with collateral</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-alert-line text-amber-600" />
                    <span className="text-gray-700">Risk of losing collateral if defaulting</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-alert-line text-amber-600" />
                    <span className="text-gray-700">Longer approval process for appraisals</span>
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Businesses with valuable assets seeking larger credit limits and lower rates
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-xl">
                    <i className="ri-file-shield-line text-purple-600 text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Unsecured Line of Credit</h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Based solely on creditworthiness, revenue, and business strength without requiring collateral. Faster approval but typically higher costs.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">No collateral required</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">Faster approval process</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-check-line text-emerald-600" />
                    <span className="text-gray-700">No risk to business assets</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-alert-line text-amber-600" />
                    <span className="text-gray-700">Higher interest rates (12-25% typical)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-alert-line text-amber-600" />
                    <span className="text-gray-700">Lower credit limits available</span>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Businesses with strong credit seeking quick access without pledging assets
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Common questions about business lines of credit
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden group"
                >
                  <summary className="px-8 py-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-100 transition-colors">
                    <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                    <i className="ri-add-line text-2xl text-indigo-600 group-open:rotate-45 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Explore Line of Credit Options?</h2>
            <p className="text-xl mb-8 text-white/90">
              Complete our pre-qualification form to see what line of credit options may be available for your business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/prequalification"
                onClick={() => track('cta_clicked', { cta: 'bottom_prequalification', funding_type: 'line_of_credit' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap shadow-xl"
              >
                Start Pre-Qualification
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/business-funding"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                View All Funding Types
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
